import { http, HttpResponse, delay } from 'msw';

import { ProductResponseDto } from '@externalTypes/general';

import {
  allProducts,
  allProductsProfile,
  allNewProductsFound,
  allProductsFound,
  manufacturers,
  productAIDescription,
  productMarginRules,
  productSuggestions,
  productTypes,
} from '../data/productsData';

export const productHandlers = [
  http.get('*/v3/products', async ({ request }) => {
    const url = new URL(request.url);
    const productType = url.searchParams.get('product_type');
    const limit = Number.parseInt(url.searchParams.get('limit') || '');

    // filtering only by type as example
    const results = allProducts.filter(
      product => product.product_type.name === productType
    );

    const response: ProductResponseDto = {
      paginated_response: {
        total: results.length,
        results: results.slice(0, limit - 1), // returning only the first page (offset = 0)
      },
      filters: {
        laptop: {
          manufacturer: ['Apple', 'Lenovo'],
          price_range: [
            '$999.99_AND_LESS',
            '$1,000_$1,999.99',
            '$2,000_$2,999.99',
            '$3,000_AND_UP',
          ],
          ram_range: ['UNDER_16_GB', 'RANGE_17_TO_35_GB', 'OVER_36_GB'],
          size_range: ['UP_TO_13.9', 'FROM_14_TO_15.9', 'OVER_16'],
          processor: [
            'Apple M2',
            'Apple M4',
            'Apple M4 Max chip',
            'Apple M4 Pro Chip',
            'Core i5',
            'Core i7',
          ],
          ssd_range: ['UP_TO_256_GB', 'RANGE_257_TO_999_GB', 'OVER_1000_GB'],
        },
        desktop: {
          manufacturer: ['Dell', 'HP', 'Lenovo'],
          price_range: [
            '$999.99_AND_LESS',
            '$1,000_$1,999.99',
            '$2,000_$2,999.99',
            '$3,000_AND_UP',
          ],
          ram_range: ['UNDER_16_GB', 'RANGE_17_TO_35_GB', 'OVER_36_GB'],
          size_range: ['UP_TO_13.9', 'FROM_14_TO_15.9', 'OVER_16'],
          ssd_range: ['UP_TO_256_GB', 'RANGE_257_TO_999_GB', 'OVER_1000_GB'],
        },
      },
    };

    await delay();
    return HttpResponse.json(response, { status: 200 });
  }),

  http.get('*/v3/products/catalog', async ({ request }) => {
    const url = new URL(request.url);
    const limit = Number.parseInt(url.searchParams.get('limit') || '');

    const response = {
      total: allProductsProfile.length,
      results: allProductsProfile.slice(0, limit - 1), // returning only the first page (offset = 0)
    };

    await delay();
    return HttpResponse.json(response, { status: 200 });
  }),

  http.post('*/v3/products/catalog', async () => {
    await delay();
    return HttpResponse.json({ id: '12345' }, { status: 200 });
  }),

  http.get('*/v3/products/manufacturers', async () => {
    await delay();
    return HttpResponse.json(manufacturers, { status: 200 });
  }),

  http.get('*/v3/products/types', async () => {
    await delay();
    return HttpResponse.json(productTypes, { status: 200 });
  }),

  http.get('*/v3/organizations/:id/products/types', async () => {
    await delay();
    return HttpResponse.json(productTypes, { status: 200 });
  }),

  http.get(
    '*/v3/organizations/:id/employee/:employeeId/products/suggestions',
    async () => {
      await delay();
      return HttpResponse.json(productSuggestions, { status: 200 });
    }
  ),

  http.get('*/v3/internal/products/:id/margin_rules', async () => {
    await delay();
    return HttpResponse.json(productMarginRules, { status: 200 });
  }),

  http.put('*/v3/internal/products/margin_rules', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.delete('*/v3/internal/products/margin_rules/:ruleId', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.get('*/v3/products/catalog/download', async () => {
    await delay();
    return new HttpResponse(
      'id,name,vendor_sku,price\n12345,Dell Laptop Latitude,11111,1299\n',
      { status: 200, headers: { 'Content-Type': 'text/csv' } }
    );
  }),

  http.get('*/v3/products/:id', async () => {
    await delay();
    return HttpResponse.json(allProducts[0], { status: 200 });
  }),

  http.get('*/v3/products/catalog/staging/search_by_sku/:sku', async () => {
    await delay();
    return HttpResponse.json(allNewProductsFound, { status: 200 });
  }),

  http.get('*/v3/products/catalog/search_by_sku/:sku', async () => {
    await delay();
    return HttpResponse.json(allProductsFound, { status: 200 });
  }),

  http.get('*/v3/products/catalog/:id', async () => {
    await delay();
    return HttpResponse.json(allProductsProfile[0], { status: 200 });
  }),

  http.patch('*/v3/products/catalog/:id', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.get('*/v3/products/catalog/:id/ai_description', async () => {
    await delay();
    return HttpResponse.json(productAIDescription, { status: 200 });
  }),

  http.post('*/v3/products/catalog/:id/images', async ({ params }) => {
    await delay();
    return HttpResponse.json(
      [
        {
          id: 'mock-product-image',
          product_id: String(params.id),
          is_primary: false,
        },
      ],
      { status: 201 }
    );
  }),

  http.patch(
    '*/v3/products/catalog/:id/images/:imageId/set_primary',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.delete('*/v3/products/catalog/:id/images/:imageId', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.post('*/v3/products/catalog/:id/related_products', async () => {
    await delay();
    return new HttpResponse(null, { status: 200 });
  }),

  http.patch('*/v3/products/catalog/:id/related_products/unlink', async () => {
    await delay();
    return new HttpResponse(null, { status: 200 });
  }),

  http.patch('*/v3/products/catalog/:id/related_products/default', async () => {
    await delay();
    return new HttpResponse(null, { status: 200 });
  }),
];
